import { Alert, Grid, LinearProgress } from '@barracuda-internal/bds-core'
import {
  EmailLog as BdsEmailLog,
  EmailLogProvider,
  IEmailLogEventListener,
  useEmailLog
} from '@barracuda-internal/converged-email-log'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import { EmailLogEventType } from '@barracuda-internal/converged-email-log/dist/app/events/emailLogEvent'

import styles from 'components/pages/emailLog/emailLogStyles'
import { useEmailLogLogic } from 'components/pages/emailLog/useEmailLogLogic'
import appConfig from 'config/appConfig'
import { useAppDispatch } from 'redux/toolkit/hooks'
import { setAccountSwitcherEvent } from 'redux/features/app/appSlice'
import { useBccAuthToken } from 'lib/hooks/useBccAuthToken'
import { useBccAuthErrorNotifications } from 'lib/hooks/useBccAuthErrorNotifications'
import { useEmailLogBridge } from '../../../EmailLogBridge'

const EmailLogRenderer = () => {
  const classes = styles()
  const emailLogBridge = useEmailLogBridge()
  const emailLogApp = useEmailLog()

  useEffect(() => {
    emailLogBridge.setApp(emailLogApp)
  }, [emailLogBridge, emailLogApp])

  return useMemo(
    () => (
      <Grid container className={classes.wrapper} data-testid="container">
        <BdsEmailLog />
      </Grid>
    ),
    [classes.wrapper]
  )
}

const EmailLog: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const uiState = useEmailLogLogic()

  // Auto-trigger OAuth2 auth when AI service is enabled and auth is needed
  const autoTriggerAuth = uiState.state === 'ready' && uiState.data.aiServiceFlagEnabled

  const { token, isAuthenticated } = useBccAuthToken({ autoTriggerAuth })

  // Handle OAuth callback errors and session validation failure notifications
  useBccAuthErrorNotifications()

  const onEmailLogEvent: IEmailLogEventListener = useCallback(
    evt => {
      // eslint-disable-next-line no-console
      console.log(evt)
      if (evt.type === EmailLogEventType.NAVIGATED && uiState.state === 'ready') {
        const nextPath = `${uiState.data.basePath}${evt.payload.path}`
        navigate(nextPath)
      }
      if (evt.type === EmailLogEventType.ACCOUNT_SWITCHER_MODE_SET) {
        dispatch(setAccountSwitcherEvent(evt))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uiState]
  )

  // Determine if AI service should be enabled based on OAuth2 authentication
  const enableAiService = uiState.state === 'ready' && uiState.data.aiServiceFlagEnabled && isAuthenticated

  // Create a getter function that returns the current token (undefined when not authenticated)
  const getToken = useCallback(() => token, [token])

  return useMemo(() => {
    switch (uiState.state) {
      case 'ready':
        return (
          <>
            <Helmet>
              <title>Email Protection</title>
            </Helmet>
            <EmailLogProvider
              bccAccountId={uiState.data.bccAccountId}
              emailLogApiClient={uiState.data.emailLogApiClient}
              language={uiState.data.emailLogLanguage}
              onEmailLogEvent={onEmailLogEvent}
              mixpanelConfig={uiState.data.mixpanelConfig}
              uiSettingsStorageKey="__CML_UI_SETTINGS"
              ipirConfig={{
                setupURL: appConfig.CONVERGED_EMAIL_LOG.IPIR_CONFIG.SETUP_URL
              }}
              disableMultiAccountSearch={!uiState.data.shouldShowMultiAccountSearch}
              baseUrl={uiState.data.baseUrl}
              userInfo={uiState.data.userInfo}
              envVars={uiState.data.envVars}
              enableEmailPolicies={uiState.data.enableEmailPolicies}
              EGDUrl={window.location.origin}
              gatewayUrl={uiState.data.gatewayUrl}
              enableRiskyConfig={uiState.data.enableEmailPoliciesRiskyConfig}
              enableMultiAccountRiskyPolicy={uiState.data.enableMultiAccountRiskyPolicy}
              aiServiceConfig={{
                baseUrl: appConfig.CONVERGED_EMAIL_LOG.AI_CONFIG.BASE_URL,
                enabled: enableAiService,
                getToken
              }}
              advancedSearchCapability={uiState.data.advancedSearchCapability}
            >
              <EmailLogRenderer />
            </EmailLogProvider>
          </>
        )
      case 'error':
        return (
          <Alert severity="error" data-testid="email-log-error">
            Failed to load email log. Please try again later.
          </Alert>
        )
      default: {
        return <LinearProgress />
      }
    }
  }, [onEmailLogEvent, uiState, enableAiService, getToken])
}

export default EmailLog
